import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { screenConfigAPI } from '../services/api';
import Lottie from 'lottie-react';
import Icon from '../components/Icon';

const styles = {
  container: {
    width: '100%'
  },
  header: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '2px'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid #e2e8f0',
    gap: '12px'
  },
  searchInput: {
    flex: 1,
    maxWidth: '300px',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#1e293b'
  },
  screenId: {
    fontFamily: 'Monaco, Consolas, monospace',
    fontSize: '13px',
    color: '#6366f1',
    backgroundColor: '#eef2ff',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  activeStatus: {
    backgroundColor: '#dcfce7',
    color: '#166534'
  },
  inactiveStatus: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px'
  },
  iconButton: {
    padding: '8px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    transition: 'all 0.15s ease'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#64748b'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '500',
    marginBottom: '8px'
  },
  emptySubtext: {
    fontSize: '14px'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b'
  },
  modalBody: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '200px',
    fontFamily: 'Monaco, Consolas, monospace',
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    boxSizing: 'border-box'
  },
  button: {
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none'
  },
  primaryButton: {
    backgroundColor: '#4f46e5',
    color: '#fff'
  },
  secondaryButton: {
    backgroundColor: '#f1f5f9',
    color: '#475569'
  },
  errorBox: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
    fontSize: '14px'
  },
  fileInput: {
    display: 'none'
  },
  uploadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#f1f5f9',
    border: '1px dashed #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#475569',
    cursor: 'pointer',
    marginBottom: '8px'
  },
  previewContainer: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px'
  },
  lottiePreview: {
    maxWidth: '200px',
    maxHeight: '200px'
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  viewModalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  jsonViewer: {
    width: '100%',
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '12px',
    fontFamily: 'Monaco, Consolas, monospace',
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    boxSizing: 'border-box',
    overflowX: 'auto',
    whiteSpace: 'pre',
    maxHeight: '500px',
    overflowY: 'auto'
  },
  copyButton: {
    padding: '8px 16px',
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '16px'
  },
  infoCard: {
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    textAlign: 'center'
  },
  infoLabel: {
    fontSize: '11px',
    color: '#64748b',
    marginBottom: '4px'
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b'
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
    backgroundColor: '#f1f5f9',
    padding: '4px',
    borderRadius: '10px'
  },
  tab: {
    flex: 1,
    padding: '10px 20px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  activeTab: {
    backgroundColor: '#fff',
    color: '#1e293b',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  inactiveTab: {
    backgroundColor: 'transparent',
    color: '#64748b'
  },
  phoneContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    minHeight: '500px'
  },
  phoneFrame: {
    width: '280px',
    height: '580px',
    backgroundColor: '#1a1a1a',
    borderRadius: '40px',
    padding: '12px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 0 0 2px #333',
    position: 'relative'
  },
  phoneNotch: {
    position: 'absolute',
    top: '12px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '120px',
    height: '28px',
    backgroundColor: '#1a1a1a',
    borderRadius: '0 0 20px 20px',
    zIndex: 10
  },
  phoneScreen: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '32px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  phoneStatusBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '44px',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#1a1a1a'
  },
  lottieContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

function ScreenManagement() {
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [formData, setFormData] = useState({
    screenId: '',
    name: '',
    description: '',
    lottieJson: '',
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lottiePreview, setLottiePreview] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewScreen, setViewScreen] = useState(null);
  const [copied, setCopied] = useState(false);
  const [viewTab, setViewTab] = useState('preview');
  const fileInputRef = useRef(null);

  const fetchScreens = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;

      const response = await screenConfigAPI.getAll(params);
      setScreens(response.data);
    } catch (err) {
      console.error('Error fetching screens:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchScreens();
  }, [fetchScreens]);

  const handleAdd = () => {
    setEditingScreen(null);
    setFormData({
      screenId: '',
      name: '',
      description: '',
      lottieJson: '',
      isActive: true
    });
    setLottiePreview(null);
    setError('');
    setShowModal(true);
  };

  const handleEdit = async (screen) => {
    try {
      // Fetch full screen data including lottieJson
      const response = await screenConfigAPI.getById(screen.id);
      const fullScreen = response.data;

      setEditingScreen(fullScreen);
      setFormData({
        screenId: fullScreen.screenId,
        name: fullScreen.name,
        description: fullScreen.description || '',
        lottieJson: fullScreen.lottieJson,
        isActive: fullScreen.isActive
      });

      // Try to parse for preview
      try {
        const parsed = JSON.parse(fullScreen.lottieJson);
        setLottiePreview(parsed);
      } catch {
        setLottiePreview(null);
      }

      setError('');
      setShowModal(true);
    } catch (err) {
      alert('Failed to load screen details');
    }
  };

  const handleDelete = async (screen) => {
    if (!window.confirm(`Are you sure you want to delete "${screen.name}"?`)) return;

    try {
      await screenConfigAPI.delete(screen.id);
      fetchScreens();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete screen');
    }
  };

  const handleToggleStatus = async (screen) => {
    try {
      await screenConfigAPI.toggleStatus(screen.id);
      fetchScreens();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle status');
    }
  };

  const handleView = async (screen) => {
    try {
      const response = await screenConfigAPI.getById(screen.id);
      setViewScreen(response.data);
      setCopied(false);
      setViewTab('preview');
      setShowViewModal(true);
    } catch (err) {
      alert('Failed to load screen details');
    }
  };

  const parsedLottieData = useMemo(() => {
    if (!viewScreen?.lottieJson) return null;
    try {
      return JSON.parse(viewScreen.lottieJson);
    } catch {
      return null;
    }
  }, [viewScreen?.lottieJson]);

  const handleCopyJson = () => {
    if (viewScreen?.lottieJson) {
      navigator.clipboard.writeText(viewScreen.lottieJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatJson = (jsonString) => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const getLottieInfo = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return {
        version: parsed.v || 'N/A',
        width: parsed.w || 'N/A',
        height: parsed.h || 'N/A',
        frames: parsed.op || 'N/A',
        fps: parsed.fr || 'N/A',
        layers: parsed.layers?.length || 0
      };
    } catch {
      return null;
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = event.target.result;
        const parsed = JSON.parse(json);
        setFormData(prev => ({ ...prev, lottieJson: json }));
        setLottiePreview(parsed);
        setError('');
      } catch {
        setError('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  const handleLottieJsonChange = (value) => {
    setFormData(prev => ({ ...prev, lottieJson: value }));
    try {
      const parsed = JSON.parse(value);
      setLottiePreview(parsed);
      setError('');
    } catch {
      setLottiePreview(null);
    }
  };

  const handleSave = async () => {
    try {
      setError('');
      setSaving(true);

      // Validate required fields
      if (!formData.screenId || !formData.name || !formData.lottieJson) {
        setError('Screen ID, name, and Lottie JSON are required');
        setSaving(false);
        return;
      }

      // Validate JSON
      try {
        JSON.parse(formData.lottieJson);
      } catch {
        setError('Invalid Lottie JSON format');
        setSaving(false);
        return;
      }

      if (editingScreen) {
        await screenConfigAPI.update(editingScreen.id, formData);
      } else {
        await screenConfigAPI.create(formData);
      }

      setShowModal(false);
      fetchScreens();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save screen');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Screen Lottie Animations</h1>
          <p style={styles.subtitle}>Manage Lottie JSON animations for mobile app screens</p>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input
            type="text"
            placeholder="Search screens..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
          <button style={styles.addButton} onClick={handleAdd}>
            <span>+</span> Add Screen
          </button>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading...</div>
        ) : screens.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}><Icon name="video" size={40} /></div>
            <div style={styles.emptyText}>No screens found</div>
            <div style={styles.emptySubtext}>Add your first Lottie animation screen</div>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Screen ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {screens.map((screen) => (
                <tr key={screen.id}>
                  <td style={styles.td}>
                    <span style={styles.screenId}>{screen.screenId}</span>
                  </td>
                  <td style={styles.td}>{screen.name}</td>
                  <td style={{ ...styles.td, color: '#64748b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {screen.description || '-'}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      ...(screen.isActive ? styles.activeStatus : styles.inactiveStatus)
                    }}>
                      {screen.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, color: '#64748b' }}>
                    {formatDate(screen.createdAt)}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <button
                        style={styles.iconButton}
                        onClick={() => handleView(screen)}
                        title="View JSON"
                      >
                        <Icon name="eye" size={15} />
                      </button>
                      <button
                        style={styles.iconButton}
                        onClick={() => handleToggleStatus(screen)}
                        title={screen.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <Icon name="dot" size={15} style={{ color: screen.isActive ? '#16a34a' : '#94a3b8' }} />
                      </button>
                      <button
                        style={styles.iconButton}
                        onClick={() => handleEdit(screen)}
                        title="Edit"
                      >
                        <Icon name="edit" size={15} />
                      </button>
                      <button
                        style={styles.iconButton}
                        onClick={() => handleDelete(screen)}
                        title="Delete"
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingScreen ? 'Edit Screen Animation' : 'Add Screen Animation'}
              </h2>
              <button
                style={{ ...styles.iconButton, fontSize: '20px' }}
                onClick={() => setShowModal(false)}
              >
                <Icon name="close" size={16} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {error && <div style={styles.errorBox}>{error}</div>}

              <div style={styles.formGroup}>
                <label style={styles.label}>Screen ID *</label>
                <input
                  type="text"
                  value={formData.screenId}
                  onChange={(e) => setFormData({ ...formData, screenId: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., splash, loading, success, error"
                  disabled={!!editingScreen}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={styles.input}
                  placeholder="Display name for this animation"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={styles.input}
                  placeholder="Brief description (optional)"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Lottie JSON *</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".json"
                  onChange={handleFileUpload}
                  style={styles.fileInput}
                />
                <label
                  style={styles.uploadButton}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon name="folder" size={15} />Upload JSON File
                </label>
                <textarea
                  value={formData.lottieJson}
                  onChange={(e) => handleLottieJsonChange(e.target.value)}
                  style={styles.textarea}
                  placeholder="Paste Lottie JSON here or upload a file..."
                />
              </div>

              {lottiePreview && (
                <div style={styles.previewContainer}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                      Valid Lottie JSON
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      Version: {lottiePreview.v || 'N/A'} |
                      Size: {lottiePreview.w}x{lottiePreview.h || 'N/A'} |
                      Frames: {lottiePreview.op || 'N/A'}
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.formGroup}>
                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <label htmlFor="isActive" style={{ fontSize: '14px', color: '#475569' }}>
                    Active (available in mobile app)
                  </label>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingScreen ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewScreen && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.viewModalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>{viewScreen.name}</h2>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  <span style={styles.screenId}>{viewScreen.screenId}</span>
                  <span style={{ marginLeft: '12px' }}>{viewScreen.description || ''}</span>
                </div>
              </div>
              <button
                style={{ ...styles.iconButton, fontSize: '20px' }}
                onClick={() => setShowViewModal(false)}
              >
                <Icon name="close" size={16} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Tabs */}
              <div style={styles.tabContainer}>
                <button
                  style={{
                    ...styles.tab,
                    ...(viewTab === 'preview' ? styles.activeTab : styles.inactiveTab)
                  }}
                  onClick={() => setViewTab('preview')}
                >
                  <Icon name="mobile" size={14} />Mobile Preview
                </button>
                <button
                  style={{
                    ...styles.tab,
                    ...(viewTab === 'json' ? styles.activeTab : styles.inactiveTab)
                  }}
                  onClick={() => setViewTab('json')}
                >
                  {'{ }'} JSON Code
                </button>
              </div>

              {/* Preview Tab */}
              {viewTab === 'preview' && (
                <div style={styles.phoneContainer}>
                  <div style={styles.phoneFrame}>
                    <div style={styles.phoneNotch} />
                    <div style={styles.phoneScreen}>
                      <div style={styles.lottieContainer}>
                        {parsedLottieData ? (
                          <Lottie
                            animationData={parsedLottieData}
                            loop={true}
                            autoplay={true}
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : (
                          <div style={{ color: '#ef4444', textAlign: 'center' }}>
                            Invalid Lottie JSON
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* JSON Tab */}
              {viewTab === 'json' && (
                <>
                  {getLottieInfo(viewScreen.lottieJson) && (
                    <div style={styles.infoGrid}>
                      <div style={styles.infoCard}>
                        <div style={styles.infoLabel}>Version</div>
                        <div style={styles.infoValue}>{getLottieInfo(viewScreen.lottieJson).version}</div>
                      </div>
                      <div style={styles.infoCard}>
                        <div style={styles.infoLabel}>Size</div>
                        <div style={styles.infoValue}>
                          {getLottieInfo(viewScreen.lottieJson).width} x {getLottieInfo(viewScreen.lottieJson).height}
                        </div>
                      </div>
                      <div style={styles.infoCard}>
                        <div style={styles.infoLabel}>Frames</div>
                        <div style={styles.infoValue}>{getLottieInfo(viewScreen.lottieJson).frames}</div>
                      </div>
                      <div style={styles.infoCard}>
                        <div style={styles.infoLabel}>Layers</div>
                        <div style={styles.infoValue}>{getLottieInfo(viewScreen.lottieJson).layers}</div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={styles.label}>Lottie JSON</label>
                    <button style={styles.copyButton} onClick={handleCopyJson}>
                      <><Icon name={copied ? 'check' : 'copy'} size={13} />{copied ? 'Copied!' : 'Copy JSON'}</>
                    </button>
                  </div>
                  <pre style={styles.jsonViewer}>
                    {formatJson(viewScreen.lottieJson)}
                  </pre>
                </>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(viewScreen);
                }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScreenManagement;
